import { useEffect } from 'react';

// Ease desktop wheel input; keep touch, keyboard, nested controls and reduced motion native.
export default function useSmoothScroll(resetKey = '') {
  useEffect(() => {
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const finePointer = window.matchMedia('(pointer: fine)');
    let frame = 0;
    let target = window.scrollY;
    let previousTime = 0;
    let settleTimer: ReturnType<typeof setTimeout> | undefined;
    const stop = () => {
      clearTimeout(settleTimer);
      cancelAnimationFrame(frame);
      frame = 0;
      previousTime = 0;
      target = window.scrollY;
    };
    const animate = (time: number) => {
      const elapsed = previousTime ? Math.min(time - previousTime, 64) : 16;
      previousTime = time;
      target = Math.min(target, Math.max(0, document.documentElement.scrollHeight - window.innerHeight));
      const distance = target - window.scrollY;
      if (Math.abs(distance) < 2) {
        window.scrollTo(0, target);
        frame = 0;
        previousTime = 0;
        return;
      }
      const step = distance * (1 - Math.exp(-elapsed / 190));
      window.scrollTo(0, window.scrollY + (Math.abs(step) < 1 ? Math.sign(distance) : step));
      frame = requestAnimationFrame(animate);
    };
    const wheel = (event: WheelEvent) => {
      if (reducedMotion.matches || !finePointer.matches || event.ctrlKey || event.metaKey || Math.abs(event.deltaX) > Math.abs(event.deltaY)) return;
      let element = event.target instanceof Element ? event.target : null;
      while (element && element !== document.body && element !== document.documentElement) {
        const style = getComputedStyle(element);
        if (element.matches('input, textarea, select, [contenteditable="true"]') || (/(auto|scroll)/.test(style.overflowY) && element.scrollHeight > element.clientHeight)) {
          stop();
          return;
        }
        element = element.parentElement;
      }
      if (!event.deltaY) return;
      event.preventDefault();
      if (!frame) target = window.scrollY;
      const unit = event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? window.innerHeight : 1;
      const max = Math.max(0, document.documentElement.scrollHeight - window.innerHeight);
      target = Math.max(0, Math.min(max, target + event.deltaY * unit * .65));
      if (!frame) frame = requestAnimationFrame(animate);
      clearTimeout(settleTimer);
      if (resetKey === '/') settleTimer = setTimeout(() => {
        if (reducedMotion.matches || !finePointer.matches) return;
        const actions = document.querySelector('.gallery-actions');
        if (actions) {
          const actionsTop = window.scrollY + actions.getBoundingClientRect().top;
          // Once the bottom links enter the intended viewport, do not pull
          // the reader back to the final image.
          if (target + window.innerHeight >= actionsTop + 44) return;
        }
        const center = (window.innerHeight + 64) / 2;
        let nearest = Infinity;
        let centeredTarget = target;
        document.querySelectorAll('.gallery-image').forEach(image => {
          const bounds = image.getBoundingClientRect();
          const destination = window.scrollY + bounds.top + bounds.height / 2 - center;
          const distance = Math.abs(destination - target);
          if (distance < nearest) { nearest = distance; centeredTarget = destination; }
        });
        // Settle near a story, while leaving the opening and footer freely scrollable.
        if (nearest < window.innerHeight * .38) {
          target = Math.max(0, Math.min(max, centeredTarget));
          if (!frame) frame = requestAnimationFrame(animate);
        }
      }, 320);
    };
    const syncNativeScroll = () => { if (!frame) target = window.scrollY; };
    const stopForKey = (event: KeyboardEvent) => {
      if (['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', ' '].includes(event.key)) stop();
    };
    window.addEventListener('wheel', wheel, { passive: false });
    window.addEventListener('scroll', syncNativeScroll, { passive: true });
    window.addEventListener('keydown', stopForKey);
    window.addEventListener('pointerdown', stop);
    window.addEventListener('resize', stop);
    reducedMotion.addEventListener('change', stop);
    return () => {
      stop();
      window.removeEventListener('wheel', wheel);
      window.removeEventListener('scroll', syncNativeScroll);
      window.removeEventListener('keydown', stopForKey);
      window.removeEventListener('pointerdown', stop);
      window.removeEventListener('resize', stop);
      reducedMotion.removeEventListener('change', stop);
    };
  }, [resetKey]);
}
